import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { LanguageService } from './language.service';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { Country, Region } from '../interfaces/country';

// Re-export for convenience
export { Country, Region } from '../interfaces/country';

// API Response types
interface CountryApiResponse<T> {
  data?: T;
  error?: boolean;
  errors?: unknown;
}

export interface Language {
  id: number;
  name: string;
  code: string;
  native_name?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CountryService {
  // Cache for countries per locale (keyed by locale code)
  private countriesCache$ = new Map<string, Observable<Country[] | null>>();

  // Cache for regions keyed by `${countryId}-${locale}`
  private regionsCache$ = new Map<string, Observable<Region[]>>();

  constructor(
    private http: HttpService,
    private languageService: LanguageService
  ) {}

  getUsedCountries(): Observable<Country[]> {
    return this.http.get<CountryApiResponse<Country[]>>('countries/used').pipe(
      map((result: CountryApiResponse<Country[]>) => {
        if (!result || result.error) {
          return [];
        }
        return result.data || [];
      })
    );
  }

  getAllCountries(): Observable<Country[] | null> {
    const locale = this.languageService.getCurrentLanguage();

    if (!this.countriesCache$.has(locale)) {
      const request$ = this.http.get<CountryApiResponse<Country[]>>('v2/countries').pipe(
        map((response: CountryApiResponse<Country[]>) => {
          if (response && response.data) {
            return response.data;
          } else {
            return null;
          }
        }),
        shareReplay(1)
      );
      this.countriesCache$.set(locale, request$);
    }

    return this.countriesCache$.get(locale)!;
  }

  getRegions(country: string | number): Observable<Region[]> {
    const locale = this.languageService.getCurrentLanguage();
    // Include locale in cache key so language changes fetch fresh translated data
    const cacheKey = `${country}-${locale}`;

    if (!this.regionsCache$.has(cacheKey)) {
      const request$ = this.http.get<CountryApiResponse<Region[] | Record<string, Region>>>(`v2/regions/${country}`).pipe(
        map((result: CountryApiResponse<Region[] | Record<string, Region>>) => {
          if (!result || result.error || result.errors || !result.data) {
            return [];
          }
          // API returns regions as an object keyed by index, convert to array
          const data = result.data;
          const regions = Array.isArray(data) ? data : Object.values(data);
          return regions as Region[];
        }),
        catchError(() => {
          return of([]);
        }),
        shareReplay(1)
      );
      this.regionsCache$.set(cacheKey, request$);
    }

    return this.regionsCache$.get(cacheKey)!;
  }

  getLanguages(): Observable<Language[] | null> {
    return this.http.get<CountryApiResponse<Language[]>>('v2/languages').pipe(
      map((response: CountryApiResponse<Language[]>) => {
        if (response && response.data) {
          return response.data;
        } else {
          return null;
        }
      })
    );
  }

  /**
   * Invalidate cached data
   * @param countryId - If provided, invalidates all locale variants for that country. Otherwise clears all caches.
   */
  invalidateCache(countryId?: string | number): void {
    if (countryId) {
      // Cache keys are stored as `${countryId}-${locale}`, so remove all locale variants
      const prefix = `${countryId}-`;
      for (const key of this.regionsCache$.keys()) {
        if (key.startsWith(prefix)) {
          this.regionsCache$.delete(key);
        }
      }
    } else {
      this.countriesCache$.clear();
      this.regionsCache$.clear();
    }
  }
}
